import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AttackRowsEditor } from "../game/AttackRowsEditor";
import { toast } from "sonner";
import { Upload } from "lucide-react";

const EMPTY = { name: "", role: "", image_url: null, ac: 10, hp_max: 10, perception: 0, attacks: [], abilities: "" };

export default function BookNpcForm({ gameId, initial, monsterTemplates, onSave, onCancel }) {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [conditions, setConditions] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/pf2e/conditions").then((r) => setConditions(r.data)).catch(() => setConditions([]));
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const importFromLibrary = (templateId) => {
    const t = monsterTemplates.find((m) => m.id === templateId);
    if (!t) return;
    setForm({
      ...form, name: t.name, image_url: t.image_url, ac: t.ac, hp_max: t.hp_max,
      perception: t.perception, attacks: t.attacks || [], abilities: t.abilities || "",
    });
  };

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post(`/uploads?game_id=${gameId}&kind=book_npc`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    setField("image_url", fileUrl(data.id));
  };

  const save = () => {
    if (!form.name.trim()) return toast.error("Give this character a name");
    onSave(form);
  };

  return (
    <div data-testid="book-npc-form" className="bg-black/80 border border-[#D4AF37]/40 rounded-xl p-4 space-y-3 backdrop-blur-md">
      <p className="text-[10px] uppercase text-[#D4AF37]/70 font-display">{initial?.id ? "Edit Character" : "Add Character to This Chapter"}</p>

      {monsterTemplates?.length > 0 && (
        <Select onValueChange={importFromLibrary}>
          <SelectTrigger data-testid="book-npc-import-select" className="bg-black/40 border-white/10 text-xs h-8">
            <SelectValue placeholder="Import stats from your Monster Library (optional)" />
          </SelectTrigger>
          <SelectContent>
            {monsterTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      <div className="flex gap-2 items-center">
        {form.image_url && <img src={form.image_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />}
        <Input data-testid="book-npc-name-input" placeholder="Name" value={form.name} onChange={(e) => setField("name", e.target.value)} className="bg-black/40 border-white/10 h-8 text-xs flex-1" />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="book-npc-image-input" />
        <Button size="sm" variant="secondary" className="h-8 w-8 rounded-full p-0 shrink-0" onClick={() => fileRef.current?.click()} data-testid="book-npc-upload-button">
          <Upload className="w-3.5 h-3.5" />
        </Button>
      </div>
      <Input data-testid="book-npc-role-input" placeholder="Role (e.g. Tavern Keeper, Boss)" value={form.role} onChange={(e) => setField("role", e.target.value)} className="bg-black/40 border-white/10 h-8 text-xs" />

      <div className="grid grid-cols-3 gap-1.5">
        <Input data-testid="book-npc-ac-input" type="number" placeholder="AC" value={form.ac} onChange={(e) => setField("ac", parseInt(e.target.value, 10) || 0)} className="bg-black/40 border-white/10 h-8 text-xs" />
        <Input data-testid="book-npc-hp-input" type="number" placeholder="HP" value={form.hp_max} onChange={(e) => setField("hp_max", parseInt(e.target.value, 10) || 0)} className="bg-black/40 border-white/10 h-8 text-xs" />
        <Input data-testid="book-npc-perception-input" type="number" placeholder="Perception" value={form.perception} onChange={(e) => setField("perception", parseInt(e.target.value, 10) || 0)} className="bg-black/40 border-white/10 h-8 text-xs" />
      </div>

      <AttackRowsEditor attacks={form.attacks} onChange={(attacks) => setField("attacks", attacks)} conditions={conditions} testIdPrefix="book-npc" />

      <Textarea data-testid="book-npc-abilities-input" placeholder="Special abilities / roleplay notes" value={form.abilities} onChange={(e) => setField("abilities", e.target.value)} className="bg-black/40 border-white/10 text-xs min-h-16" />

      <div className="flex gap-2">
        <Button data-testid="book-npc-save-button" onClick={save} size="sm" className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f]">Save Character</Button>
        <Button data-testid="book-npc-cancel-button" onClick={onCancel} size="sm" variant="secondary" className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
}
